import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useSentinelChat } from './SentinelChatProvider'
import StatsRow from './StatsRow'
import ResultChart from './ResultChart'
import ResultTable from './ResultTable'
import TurnActions from './TurnActions'
import ClarifyingQuestion from './ClarifyingQuestion'

// A result whose table has more columns than this breaks out of the narrow
// chat lane to the full width, so users don't have to scroll it sideways.
const WIDE_COLUMN_COUNT = 6

function hasChartSpec(chart) {
  return !!(chart && chart.type && chart.type !== 'none' && chart.type !== 'null')
}

function Working() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 fd-body-sm" style={{ color: 'var(--text-tertiary)', padding: '4px 0' }}>
      <span className="flex gap-1" aria-hidden="true">
        <span className="fd-pulse" style={{ width: 6, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--fd-teal-400)' }} />
        <span className="fd-pulse" style={{ width: 6, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--fd-teal-400)', animationDelay: '.2s' }} />
        <span className="fd-pulse" style={{ width: 6, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--fd-teal-400)', animationDelay: '.4s' }} />
      </span>
      {t('Sentinel.V2.working')}
    </div>
  )
}

/**
 * Renders a single assistant turn. The user's question is shown in the
 * preceding user bubble, so this leads with the answer. Text, stats and charts
 * stay in the narrow chat lane; a wide-column table breaks the whole message
 * out to full width (via `fd-msg--wide`) while keeping those blocks centered.
 * The audience drill opens the conversation-level inspector via `openInspector`.
 */
export default function AssistantMessage({ turn }) {
  const { t } = useTranslation()
  const { rerunTurn, openInspector, inspectorCustomerId } = useSentinelChat()

  const chartTitle = turn.chart?.title
  const rowCount = turn.table?.row_count ?? turn.table?.rows?.length ?? 0
  // Sentinel caps every result set and reports whether that cap cut the answer
  // short. Without the notice a capped answer looks complete: the row count reads
  // as the real total and everything past the cap is lost without a word.
  const truncated = turn.table?.truncated === true
  const rowLimit = turn.table?.row_limit ?? rowCount
  const isResult = turn.status === 'result'
  const columnCount = turn.table?.columns?.length ?? 0
  const wide = isResult && columnCount > WIDE_COLUMN_COUNT

  return (
    <div className={`fd-msg fd-msg--assistant${wide ? ' fd-msg--wide' : ''}`}>
      {isResult ? (
        <>
          <div className="fd-msg__narrow">
            {chartTitle && <h3 className="fd-h4" style={{ lineHeight: 1.3, marginBottom: '4px' }}>{chartTitle}</h3>}
            {turn.explanation && (
              <div
                className="fd-msg__explanation"
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 14px',
                  marginBottom: '12px',
                }}
              >
                <i
                  className="fa-solid fa-circle-info"
                  aria-hidden="true"
                  style={{ color: 'var(--fd-teal-600)', fontSize: '13px', marginTop: '3px', flex: '0 0 auto' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div className="fd-eyebrow" style={{ marginBottom: '3px' }}>{t('Sentinel.V2.explanationLabel')}</div>
                  <p className="fd-body-sm" style={{ color: 'var(--text-secondary)' }}>{turn.explanation}</p>
                </div>
              </div>
            )}
            <p className="fd-body-sm" style={{ color: 'var(--text-tertiary)', marginBottom: truncated ? '6px' : '12px' }}>
              {t('Sentinel.V2.rows', { n: rowCount.toLocaleString() })}
            </p>
            {truncated && (
              <p
                className="fd-body-sm"
                style={{ color: 'var(--text-warning, var(--text-secondary))', marginBottom: '12px' }}
                role="status"
              >
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" style={{ marginRight: '6px' }} />
                {t('Sentinel.V2.truncated', { n: rowLimit.toLocaleString() })}
              </p>
            )}
            <StatsRow aggregates={turn.aggregates} />
            {hasChartSpec(turn.chart) && <ResultChart table={turn.table} chart={turn.chart} />}
          </div>

          <ResultTable
            table={turn.table}
            isAudience={turn.isAudience}
            onOpenCustomer={openInspector}
            selectedCustomerId={inspectorCustomerId}
          />

          <div className="fd-msg__narrow">
            <TurnActions turn={turn} />
          </div>
        </>
      ) : (
        <>
          {chartTitle && <h3 className="fd-h4" style={{ lineHeight: 1.3, marginBottom: '4px' }}>{chartTitle}</h3>}

          {turn.status === 'pending' && <Working />}

          {turn.status === 'clarification' && <ClarifyingQuestion question={turn.clarifyingQuestion} />}

          {turn.status === 'error' && (
            <div className="fd-toast fd-toast--danger" role="alert" style={{ maxWidth: '640px' }}>
              <div>
                <div className="fd-toast__title">{t('Sentinel.V2.error.title')}</div>
                <div className="fd-toast__body">{turn.message || t('Sentinel.V2.error.fallback')}</div>
              </div>
            </div>
          )}

          {turn.status === 'history' && (
            <div className="fd-card" style={{ maxWidth: '640px' }}>
              <p className="fd-body-sm" style={{ marginBottom: '12px' }}>
                {t('Sentinel.V2.history.notStored')}
                {turn.rowCount != null && (
                  <span className="fd-mono" style={{ color: 'var(--text-tertiary)' }}> {t('Sentinel.V2.history.rowsPreviously', { n: turn.rowCount })}</span>
                )}
              </p>
              <button className="fd-btn fd-btn--sm" onClick={() => rerunTurn(turn.id, turn.question)}>{t('Sentinel.V2.rerun')}</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

AssistantMessage.propTypes = {
  turn: PropTypes.object.isRequired,
}
